import { useNavigate } from "react-router-dom";

import type { FleetUnit } from "@/api/fleet";
import { formatDate } from "@/lib/utils";

interface Props {
  unit: FleetUnit | null;
  onClose: () => void;
}

export function UnitDetailDrawer({ unit, onClose }: Props) {
  const nav = useNavigate();
  if (!unit) return null;

  const kpi = unit.kpiToday;

  return (
    <aside
      className="absolute right-0 top-0 h-full w-96 border-l border-border bg-surface shadow-2xl overflow-y-auto"
      data-testid="fleet-drawer"
    >
      <header className="px-4 py-3 border-b border-border flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-sm">{unit.alias}</h3>
          <p className="text-xs text-muted-200">{unit.placa ?? "—"}</p>
        </div>
        <button className="btn-ghost" onClick={onClose}>
          ×
        </button>
      </header>

      <div className="p-4 text-sm space-y-2">
        <Row label="Estado" value={unit.estado} />
        <Row label="Operador" value={unit.operadorNombre ?? "—"} />
        <Row label="Operativo" value={unit.operativoNombre ?? "—"} />
        <Row
          label="Velocidad"
          value={
            unit.speedKmh != null ? `${Math.round(unit.speedKmh)} km/h` : "—"
          }
        />
        <Row
          label="Rumbo"
          value={unit.heading != null ? `${Math.round(unit.heading)}°` : "—"}
        />
        <Row
          label="Coords"
          value={
            unit.latitude != null && unit.longitude != null
              ? `${unit.latitude.toFixed(5)}, ${unit.longitude.toFixed(5)}`
              : "—"
          }
        />
        <Row label="Último latido" value={formatDate(unit.lastHeartbeatAt)} />

        {unit.sesionActiva && (
          <div className="rounded-md bg-emerald-500/10 border border-emerald-500/30 p-2 text-xs">
            <div className="text-emerald-300 font-medium">
              Sesión activa
            </div>
            <div className="text-muted-200">
              Inicio: {formatDate(unit.sesionActiva.inicio)} —{" "}
              {unit.sesionActiva.minutosActiva} min
            </div>
          </div>
        )}

        {unit.pausaActiva && (
          <div className="rounded-md bg-amber-500/10 border border-amber-500/30 p-2 text-xs">
            <div className="text-amber-300 font-medium">
              Pausa abierta {unit.pausaActiva.minutosPausa} min
            </div>
            <div className="text-muted-200">
              {unit.pausaActiva.motivoNombre ??
                `Motivo #${unit.pausaActiva.motivoId}`}
            </div>
          </div>
        )}

        {unit.lastHit && (
          <div className="rounded-md bg-rose-500/10 border border-rose-500/30 p-2 text-xs">
            <div className="text-rose-300 font-medium">
              Último hit · {unit.lastHit.fase}
            </div>
            <div className="text-muted-200">
              {unit.lastHit.placa ?? unit.lastHit.vin ?? "—"} ·{" "}
              {formatDate(unit.lastHit.at)}
            </div>
          </div>
        )}

        {kpi && (
          <div className="grid grid-cols-2 gap-2 mt-3">
            <KpiTile label="Lecturas" value={kpi.readings} />
            <KpiTile label="Hits" value={kpi.hits} tone="danger" />
            <KpiTile
              label="LPR"
              value={kpi.lprEvents}
            />
            <KpiTile
              label="Distancia"
              value={`${kpi.distanciaKm.toFixed(1)} km`}
            />
            <KpiTile
              label="En movimiento"
              value={formatSeconds(kpi.tiempoMovimientoSeconds)}
            />
            <KpiTile
              label="Detenida"
              value={formatSeconds(kpi.tiempoDetenidoSeconds)}
            />
          </div>
        )}

        <button
          className="btn-primary w-full mt-3"
          onClick={() => nav(`/units/${unit.unitId}`)}
          data-testid={`fleet-drawer-detail-${unit.unitId}`}
        >
          Abrir detalle
        </button>
      </div>
    </aside>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-xs text-muted-200">{label}</span>
      <span className="text-sm">{value}</span>
    </div>
  );
}

function KpiTile({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: React.ReactNode;
  tone?: "neutral" | "danger";
}) {
  const toneClass =
    tone === "danger" && Number(value) > 0
      ? "text-rose-400"
      : "text-foreground";
  return (
    <div className="rounded-md bg-surface2/40 px-2 py-1">
      <div className="text-[10px] uppercase tracking-wide text-muted-200">
        {label}
      </div>
      <div className={`text-sm font-semibold ${toneClass}`}>{value}</div>
    </div>
  );
}

function formatSeconds(seconds: number): string {
  if (!seconds || seconds <= 0) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}
